import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";

import appStore from "../storage/AppStore";
import systemStore from "../storage/SystemStore";

import Select from "./Select";


const CountrySelect = ({
    store,
    layout = "column",
    columns = 3,
    className = "",
}) => {
    const [regions, setRegions] = useState([]);
    const [districts, setDistricts] = useState([]);

    const [countriesLoading, setCountriesLoading] = useState(false);
    const [regionsLoading, setRegionsLoading] = useState(false);
    const [districtsLoading, setDistrictsLoading] = useState(false);


    const setStoreProperty = (prop, value) => {
        if (typeof store.setProperty === "function") {
            store.setProperty(prop, value);
        } else {
            store[prop] = value;
        }
    };


    const handleError = (error) => {
        systemStore.setError(
            appStore.tr[error?.message] ||
            error?.message ||
            "Error"
        );
    };


    /* =====================================================
       COUNTRIES
       ===================================================== */

    useEffect(() => {
        if (appStore.countries?.length) {
            return;
        }

        let cancelled = false;

        const loadCountries = async () => {
            setCountriesLoading(true);

            try {
                const response =
                    await appStore.getCountries();

                if (cancelled) {
                    return;
                }

                appStore.setCountries(
                    response?.countries || []
                );
            } catch (error) {
                if (!cancelled) {
                    handleError(error);
                }
            } finally {
                if (!cancelled) {
                    setCountriesLoading(false);
                }
            }
        };

        loadCountries();

        return () => {
            cancelled = true;
        };
    }, []);


    /* =====================================================
       REGIONS
       ===================================================== */

    useEffect(() => {
        const countryId =
            store.country_id;

        let cancelled = false;


        if (!countryId) {
            setRegions([]);
            setRegionsLoading(false);

            return () => {
                cancelled = true;
            };
        }


        const loadRegions = async () => {
            setRegionsLoading(true);

            try {
                const response =
                    await appStore.getRegions(
                        countryId
                    );

                if (
                    cancelled ||
                    store.country_id !== countryId
                ) {
                    return;
                }

                setRegions(
                    response?.items || []
                );
            } catch (error) {
                if (!cancelled) {
                    handleError(error);
                }
            } finally {
                if (!cancelled) {
                    setRegionsLoading(false);
                }
            }
        };


        loadRegions();


        return () => {
            cancelled = true;
        };
    }, [
        store,
        store.country_id,
    ]);


    /* =====================================================
       DISTRICTS
       ===================================================== */

    useEffect(() => {
        const regionId =
            store.region_id;

        let cancelled = false;


        if (!regionId) {
            setDistricts([]);
            setDistrictsLoading(false);

            return () => {
                cancelled = true;
            };
        }


        const loadDistricts = async () => {
            setDistrictsLoading(true);

            try {
                const response =
                    await appStore.getDistricts(
                        regionId
                    );

                if (
                    cancelled ||
                    store.region_id !== regionId
                ) {
                    return;
                }

                setDistricts(
                    response?.items || []
                );
            } catch (error) {
                if (!cancelled) {
                    handleError(error);
                }
            } finally {
                if (!cancelled) {
                    setDistrictsLoading(false);
                }
            }
        };


        loadDistricts();


        return () => {
            cancelled = true;
        };
    }, [
        store,
        store.region_id,
    ]);


    /* =====================================================
       CHANGE HANDLERS
       ===================================================== */

    const handleCountryChange = () => {
        setStoreProperty(
            "region_id",
            ""
        );

        setStoreProperty(
            "district_id",
            ""
        );

        setRegions([]);
        setDistricts([]);
    };


    const handleRegionChange = () => {
        setStoreProperty(
            "district_id",
            ""
        );

        setDistricts([]);
    };


    /* =====================================================
       LAYOUT
       ===================================================== */

    const isRow =
        layout === "row";


    const safeColumns =
        Math.max(
            1,
            Math.min(
                Number(columns) || 3,
                3
            )
        );


    const columnWidth = {
        1: 12,
        2: 6,
        3: 4,
    }[safeColumns];


    const fieldClassName =
        isRow
            ? `col-12 md:col-${columnWidth}`
            : "w-full";


    const rootClassName = [
        isRow
            ? "grid"
            : "flex flex-column gap-3",

        className,
    ]
        .filter(Boolean)
        .join(" ");


    return (
        <div className={rootClassName}>

            {/* COUNTRY */}

            <div className={fieldClassName}>
                <label
                    htmlFor="country"
                    className="block mb-1"
                >
                    {appStore.tr.country}
                </label>

                <Select
                    inputId="country"

                    store={store}
                    prop="country_id"

                    options={
                        appStore.countries || []
                    }

                    label={
                        appStore.tr.country
                    }

                    required

                    disabled={
                        countriesLoading
                    }

                    onChange={
                        handleCountryChange
                    }
                />
            </div>


            {/* REGION */}

            <div className={fieldClassName}>
                <label
                    htmlFor="region"
                    className="block mb-1"
                >
                    {appStore.tr.region}
                </label>

                <Select
                    inputId="region"

                    store={store}
                    prop="region_id"

                    options={regions}

                    label={
                        appStore.tr.region
                    }

                    required

                    disabled={
                        !store.country_id ||
                        regionsLoading
                    }

                    onChange={
                        handleRegionChange
                    }
                />
            </div>


            {/* DISTRICT */}

            <div className={fieldClassName}>
                <label
                    htmlFor="district"
                    className="block mb-1"
                >
                    {appStore.tr.district}
                </label>

                <Select
                    inputId="district"

                    store={store}
                    prop="district_id"

                    options={districts}

                    label={
                        appStore.tr.district
                    }

                    disabled={
                        !store.region_id ||
                        districtsLoading
                    }
                />
            </div>

        </div>
    );
};


export default observer(CountrySelect);